<script lang="ts" setup>
import { ref, inject, watch } from 'vue';
import { waitFor, getChildMeshList, freezeMeshListWorldMatrix, unfreezeMeshListWorldMatrix } from '@immersive-tech/viewer-engine';
import ToggleBox from '@/components/ToggleBox.vue';

import type { Ref } from 'vue';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestPart, ManifestPartMode } from '@/types/IManifest';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Lights } from '@/viewerExtensions';

interface IProps {
  identifier: string;
}

interface IEmits {
  (e: 'update', value: string, part: Ref<IManifestPart>): unknown;
  (e: 'delete', value: string, part: Ref<IManifestPart>): unknown;
}

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const props = defineProps<IProps>();
const emit = defineEmits<IEmits>();
const part = ref<IManifestPart>(manifest.value.parts[manifest.value.parts.findIndex((part) => part.path === props.identifier)]);
const partMode = ref(part.value.mode);
const partInvert = ref(part.value.invert ?? false);
const partVisible = ref(part.value.visible);
const lightsExtension = viewer.value?.extensions.get('Lights') as Extension<Lights>;

const partVisibleWatcher = async (newValue: boolean) => {
  if (!viewer.value) return;

  part.value.visible = newValue;

  viewer.value.renderSemaphore += 1;

  if (newValue === false) {
    viewer.value.modelContainers.get(part.value.path)?.removeAllFromScene();
    lightsExtension.hideEntityShadow(part.value.path);
  } else {
    viewer.value.modelContainers.get(part.value.path)?.addAllToScene();
    lightsExtension.showEntityShadow(part.value.path);
  }

  await waitFor(100);

  viewer.value.renderSemaphore -= 1;
};

const partInvertWatcher = (newValue: boolean) => {
  if (!viewer.value) return;

  const entity = viewer.value.entities.get(part.value.name) as Mesh;
  const meshes = [entity, ...getChildMeshList([entity])];

  part.value.invert = newValue;

  unfreezeMeshListWorldMatrix(meshes);

  entity.scaling.x = -entity.scaling.x;
  entity.scaling.z = -entity.scaling.z;

  freezeMeshListWorldMatrix(meshes);

  viewer.value.renderNextFrame += 1;
};

const partModeWatcher = (newValue: ManifestPartMode) => {
  part.value.mode = newValue;

  emit('update', props.identifier, part);
};

const deletePartButtonClick = () => {
  if (!viewer.value) return;

  manifest.value.parts.splice(manifest.value.parts.findIndex((part) => part.path === props.identifier), 1);

  if (viewer.value.entities.has(part.value.path)) {
    viewer.value.destroyModel(part.value.path);
  }

  emit('delete', props.identifier, part);
};

watch(partVisible, partVisibleWatcher);
watch(partInvert, partInvertWatcher);
watch(partMode, partModeWatcher);
</script>

<template>
  <div class="flex flex-row items-center mb-1">
    <label :for="`partconfig-mode-${part.path}`" class="text-xs mr-2">Mode</label>
    <select :id="`partconfig-mode-${part.path}`" v-model="partMode" class="text-xs border px-1 py-0.5 w-full">
      <option value="BASE">
        BASE
      </option>
      <option value="DEFERRED_ON_DEMAND">
        DEFERRED_ON_DEMAND
      </option>
      <option value="DEFERRED_IMMEDIATE">
        DEFERRED_IMMEDIATE
      </option>
    </select>
  </div>

  <div class="flex flex-row justify-between mb-1">
    <ToggleBox v-model="partVisible" label="Visible" />
    <ToggleBox v-model="partInvert" label="Invert" />
  </div>

  <div class="flex flex-col">
    <button type="button" class="text-xs p-1 bg-red-400 text-white my-1" @click="deletePartButtonClick">
      Delete
    </button>
  </div>
</template>
